import type { ProductOption } from './purchaseChoice';
import { isColourOption, swatchColours, swatchName } from './colorSwatch';
import { useShopWords } from '../../commerce/shopWords';

// The two controls Sleek's product page buys with: a row of pills per choice ("Size: 30ml"), and a quantity
// stepper beside the button. Plain buttons and an input, so a keyboard reaches and works every one of them.

/** One choice's pills: the option, the value picked, and how a pick is made. */
type OptionPillsProps = {
  option: ProductOption;
  picked: string;
  inStock: (value: string) => boolean;
  disabled: boolean;
  onPick: (value: string) => void;
};

// Draws one choice as its name, the value picked, and a pill or a colour swatch per value; one out of stock is struck through.
export const OptionPills: React.FC<OptionPillsProps> = ({ option, picked, inStock, disabled, onPick }) => {
  const colours = isColourOption(option.name) ? swatchColours(option.values) : null;
  return (
    <fieldset className={colours ? 'xe-purchase-pills xe-purchase-swatches' : 'xe-purchase-pills'}>
      <legend>{option.name}: <span>{swatchName(picked)}</span></legend>
      <div>
        {option.values.map((value) => (
          <button key={value} type="button" aria-pressed={value === picked} data-out={inStock(value) ? undefined : ''} disabled={disabled} onClick={() => onPick(value)}
            aria-label={colours ? swatchName(value) : undefined} title={colours ? swatchName(value) : undefined} style={colours ? { background: colours.get(value) } : undefined}>
            {colours ? null : value}
          </button>
        ))}
      </div>
    </fieldset>
  );
};

/** The quantity, its ceiling, and how it changes. */
type QuantityStepperProps = { value: number; max: number; disabled: boolean; onChange: (value: number) => void; name?: string };

// Draws the quantity between a minus and a plus, never below one nor past what can be ordered.
export const QuantityStepper: React.FC<QuantityStepperProps> = ({ value, max, disabled, onChange, name }) => {
  const ceiling = Math.max(1, Math.min(99, max));
  const set = (next: number) => onChange(Math.max(1, Math.min(ceiling, Math.trunc(next) || 1)));
  const words = useShopWords();
  // In a cart several steppers sit together, so each one says which product it counts, in the store's language.
  const of = name ? ` – ${name}` : '';
  return (
    <div className="xe-purchase-stepper">
      <button type="button" aria-label={`${words.decreaseQuantity}${of}`} disabled={disabled || value <= 1} onClick={() => set(value - 1)}>−</button>
      <input type="number" aria-label={`${words.quantity}${of}`} min={1} max={ceiling} value={value} disabled={disabled} onChange={(event) => set(Number(event.target.value))} />
      <button type="button" aria-label={`${words.increaseQuantity}${of}`} disabled={disabled || value >= ceiling} onClick={() => set(value + 1)}>+</button>
    </div>
  );
};
